import { PlusOutlined, HomeFilled } from "@ant-design/icons";
import { Button, Flex } from "antd";
import cloneDeep from "lodash/cloneDeep";
import compact from "lodash/compact";
import find from "lodash/find";
import findIndex from "lodash/findIndex";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import set from "lodash/set";
import toPath from "lodash/toPath";
import unset from "lodash/unset";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import DragAndDrop from "@/components/Elements/DragAndDrop";
import DragAndDropGroup from "@/components/Elements/DragAndDropGroup";
import { useSeveritySection } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/hooks/useSeveritySection";
import {
  generateFieldUniqueModel,
  generateGroupUniqueLegend,
} from "@/modules/schema-builder";
import { SchemaBuilderFieldRow } from "@/modules/schema-builder/schema-builder-field-row";
import { SchemaBuilderFormProvider } from "@/modules/schema-builder/schema-builder-form";

import Group from "./Group";
import MovableElements from "./MovableElements";
import MovableGroups from "./MovableGroups";

import "./Editor.style.less";

const Editor = ({ schema, context, onSchemaChange }) => {
  const { t } = useTranslation();
  const [isDndGroups, setIsDndGroups] = useState(false);
  const [activeNode, setActiveNode] = useState();
  const groupsEndRef = useRef(null);

  const {
    showAddSeveritySection,
    isDisabled,
    isLoading,
    updateSeveritySectionAvailability,
    buildSeverityGroup,
  } = useSeveritySection();

  /**
   * Check if schema changed and calls callback
   *
   * @param {object} propSchema
   * @return {void}
   */
  function onChange(propSchema, isRegistrable = true) {
    updateSeveritySectionAvailability(propSchema);

    if (!isEqual(propSchema, schema)) {
      onSchemaChange(propSchema, isRegistrable);
    }
  }

  useEffect(() => {
    updateSeveritySectionAvailability(schema);
  }, [schema]);

  /**
   * Add an ungrouped field to the schema
   *
   * @return {void}
   */
  function onAddUngroupedField() {
    const parentPath = "fields";

    addFieldToParent(parentPath);
  }

  /**
   * Add a grouped field to the schema
   *
   * @param {string} legend
   * @return {void}
   */
  function onAddFieldToGroup(legend) {
    const index = findIndex(schema.groups, (group) => group.legend === legend);
    const parentPath = `groups[${index}].fields`;

    addFieldToParent(parentPath);
  }

  /**
   * Add field to parent
   *
   * @param {string} parentPath
   * @return {void}
   */
  function addFieldToParent(parentPath) {
    const tempSchema = cloneDeep(schema);

    if (!tempSchema.fields) {
      tempSchema.fields = [];
    }

    const parent = get(tempSchema, parentPath);
    const model = generateFieldUniqueModel("text");
    const newField = { type: "input", inputType: "text", model };

    parent.splice(0, 0, newField);
    set(tempSchema, parentPath, parent);

    onChange(tempSchema, false);
  }

  /**
   * Delete the edited field
   *
   * @param {object} field
   * @param {string} fieldPath
   * @return {void}
   */
  function onFieldDelete(_field, fieldPath) {
    const tempSchema = cloneDeep(schema);

    // Unset field
    unset(tempSchema, fieldPath);
    // Cleanup parent
    const parentPath = toPath(fieldPath);
    parentPath.pop();
    set(tempSchema, parentPath, compact(get(tempSchema, parentPath)));

    onChange(tempSchema);
  }

  /**
   * Dupplicate a field
   *
   * @param {object} field
   * @param {string} fieldPath
   * @return {void}
   */
  function onFieldCopy(field, fieldPath) {
    const tempSchema = cloneDeep(schema);

    // Cleanup parent
    const parentPath = toPath(fieldPath);
    const copiedIndex = parseInt(parentPath.pop());

    const parent = get(tempSchema, parentPath);

    // Clone field, change unique model
    const model = generateFieldUniqueModel(field);

    const copy = { ...field, label: `${field.label} (copy)`, model };

    parent.splice(copiedIndex + 1, 0, copy);
    set(tempSchema, parentPath, parent);

    onChange(tempSchema);
  }

  /**
   * Update a field
   *
   * @param {object} modifiedField
   * @param {string} fieldPath
   * @return {void}
   */
  function onFieldUpdate(modifiedField, fieldPath) {
    const tempSchema = cloneDeep(schema);

    set(tempSchema, fieldPath, modifiedField);
    onChange(tempSchema);
  }

  /**
   * Rename a group
   *
   * @param {string} prevLegend
   * @param {string} newLegend
   * @return {void}
   */
  function onGroupRename(prevLegend, newLegend) {
    const tempSchema = cloneDeep(schema);
    find(tempSchema.groups, { legend: prevLegend }).legend = newLegend;

    onChange(tempSchema);
  }

  function scrollToBottom() {
    groupsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  function getNewGroupName(tempSchema) {
    const groups = tempSchema.groups;
    let groupLegendId = groups.length + 1;

    while (
      groups.find((group) => {
        return group.legend === `${t("global:group")} ${groupLegendId}`;
      })
    ) {
      groupLegendId++;
    }

    return `${t("global:group")} ${groupLegendId}`;
  }

  function onAddGroup() {
    const tempSchema = cloneDeep(schema);

    if (!tempSchema.groups) {
      tempSchema.groups = [
        {
          fields: [],
          legend: `${t("global:group")} 1`,
          droppable: true,
          draggable: true,
        },
      ];
    } else {
      tempSchema.groups.push({
        fields: [],
        legend: getNewGroupName(tempSchema),
        droppable: true,
        draggable: true,
      });
    }

    onChange(tempSchema);
    scrollToBottom();
  }

  function onAddSeveritySection() {
    const tempSchema = cloneDeep(schema);

    if (!tempSchema.groups) {
      tempSchema.groups = [];
    }
    tempSchema.groups.push(buildSeverityGroup());

    onChange(tempSchema);
    scrollToBottom();
  }

  /**
   * Dupplicate a group
   *
   * @param {string} legend
   * @return {void}
   */
  function onGroupCopy(legend) {
    const tempSchema = cloneDeep(schema);
    const el = find(tempSchema.groups, { legend });

    // duplicate group name (legend)
    const elCopy = cloneDeep(el);

    elCopy.legend = generateGroupUniqueLegend(elCopy.legend);

    // duplicate each field
    elCopy.fields.forEach((field) => {
      field.model = generateFieldUniqueModel(field);
    });

    // insert
    const i = findIndex(tempSchema.groups, { legend });
    if (i !== -1) tempSchema.groups.splice(i + 1, 0, elCopy);

    onChange(tempSchema);
  }

  function onDeleteGroup(groupPath) {
    const tempSchema = cloneDeep(schema);

    const filtredGroups = tempSchema.groups.filter((group) => {
      return group.legend !== groupPath;
    });

    tempSchema.groups = filtredGroups;

    onChange(tempSchema);
  }

  function dndFields() {
    return (
      <DragAndDrop.Context
        source={schema}
        sortableContainerKey="fields"
        sortableItemId="model"
        activeNode={activeNode}
        onMoveEnd={() => onChange(schema)}
        onMoveOver={(movedSchema) => onChange(movedSchema)}
        onChangeActiveNode={(node) => setActiveNode(node)}
        dragOverlay={(field) =>
          field && <SchemaBuilderFieldRow schema={field} isDragged />
        }
      >
        <MovableElements
          schema={schema}
          activeNode={activeNode}
          onGroupCopy={onGroupCopy}
          onDeleteGroup={onDeleteGroup}
          onGroupRename={onGroupRename}
          onFieldUpdate={onFieldUpdate}
          onFieldCopy={onFieldCopy}
          onFieldDelete={onFieldDelete}
          onAddField={onAddUngroupedField}
          onAddFieldToGroup={onAddFieldToGroup}
        />
      </DragAndDrop.Context>
    );
  }

  function dndGroups() {
    return (
      <DragAndDropGroup.Context
        dragOverlay={(group) => <Group.Preview isDragged dnd legend={group} />}
        schema={schema}
        setSchema={onChange}
      >
        {schema.fields && !isDndGroups && (
          <Group.Preview dnd legend={<HomeFilled />} />
        )}
        <MovableGroups
          schema={schema}
          onGroupRename={onGroupRename}
          onGroupCopy={onGroupCopy}
          onDeleteGroup={onDeleteGroup}
        />
      </DragAndDropGroup.Context>
    );
  }

  return (
    <SchemaBuilderFormProvider schema={schema} context={context}>
      <div className="form-engine-editor">
        <Flex gap="small">
          <Button
            onClick={() => setIsDndGroups(!isDndGroups)}
            className="header-button"
          >
            {isDndGroups ? t("global:sortFields") : t("global:sortGroups")}
          </Button>
          <Button
            icon={<PlusOutlined />}
            onClick={() => onAddGroup()}
            className="header-button"
          >
            {t("global:addGroup")}
          </Button>
          {showAddSeveritySection && (
            <Button
              icon={<PlusOutlined />}
              onClick={onAddSeveritySection}
              className="header-button"
              disabled={isDisabled}
              loading={isLoading}
            >
              {t("form:addSeveritySection")}
            </Button>
          )}
        </Flex>
        {isDndGroups ? dndGroups() : dndFields()}
        <div ref={groupsEndRef} />
      </div>
    </SchemaBuilderFormProvider>
  );
};

export default Editor;
